import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersApi } from '../services/api';
import type { User } from '../types';
import { ProCard } from '../components/obros/ProCard';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { SkeletonCard } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../stores/authStore';

type FilterKey = 'all' | 'near' | 'top' | 'verified';

const filters: { key: FilterKey; label: string; icon: string }[] = [
  { key: 'all', label: 'Todos', icon: 'apps' },
  { key: 'near', label: 'Cerca tuyo', icon: 'near_me' },
  { key: 'top', label: 'Mejor valorados', icon: 'star' },
  { key: 'verified', label: 'Verificados', icon: 'verified' },
];

// Calca ScreenPros de screens-main.jsx (Claude Design)
export const Workers: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [workers, setWorkers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const categoryId = searchParams.get('category_id') ?? undefined;

  const fetchPage = useCallback(
    async (pageToFetch: number, append: boolean) => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = { page: pageToFetch, search: search || undefined, category_id: categoryId };
        if (filter === 'near' && user?.city) params.city = user.city;

        const { data } = await usersApi.workers(params);
        let list: User[] = data.data;
        if (filter === 'top') list = [...list].sort((a, b) => (Number(b.rating_average) || 0) - (Number(a.rating_average) || 0));
        if (filter === 'verified') list = list.filter((w) => w.verification_status === 'approved');

        setWorkers((prev) => (append ? [...prev, ...list] : list));
        setTotal(data.total);
        setLastPage(data.last_page);
        setPage(pageToFetch);
      } finally {
        setLoading(false);
      }
    },
    [search, filter, categoryId, user?.city]
  );

  useEffect(() => {
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, categoryId]);

  return (
    <div className="bg-cream2 min-h-full">
      <div className="max-w-xl mx-auto px-4 py-5">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-onSurface">Profesionales</h1>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchPage(1, false);
            }}
          >
            <div className="flex items-center h-10 rounded-full bg-surface border border-outlineVarWarm px-3.5 gap-2">
              <MaterialIcon name="search" size={16} className="text-onSurface-mute" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar"
                className="w-28 sm:w-40 bg-transparent outline-none text-sm text-onSurface placeholder-onSurface-mute"
              />
            </div>
          </form>
        </div>

        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mb-4">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-1.5 shrink-0 rounded-full border px-3.5 py-2 text-[13px] font-semibold whitespace-nowrap transition-all ${
                filter === f.key
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'bg-surface border-outlineVarWarm text-onSurface hover:border-primary-300'
              }`}
            >
              <MaterialIcon name={f.icon} size={16} />
              {f.label}
            </button>
          ))}
        </div>

        <p className="text-[13px] text-onSurface-mute mb-3">{total} profesionales encontrados</p>

        <div className="space-y-3">
          {loading && workers.length === 0
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            : workers.map((w, i) => <ProCard key={w.id} worker={w} rank={filter === 'top' ? i + 1 : undefined} />)}
        </div>

        {!loading && workers.length === 0 && (
          <p className="text-center text-onSurface-mute py-16">No encontramos profesionales con esos filtros.</p>
        )}

        {page < lastPage && (
          <div className="flex justify-center mt-6">
            <Button variant="outlined" onClick={() => fetchPage(page + 1, true)} isLoading={loading}>
              Cargar más
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
